<template>
  <b-form-group>
    <date-range-picker
        ref="daterange-picker"
        v-model="dates"
        style="width: 100%; height: 36px"
        :append-to-body="appendToBody"
        :locale-data="localeData"
        :ranges="shortcuts"
        :linked-calendars="false"
        :placeholder="placeholder"
        v-bind="$attrs"
        :class="{'invalid': state === false}"
        :opens="opens"
    >
      <template v-slot:input="{ startDate, endDate, rangeText }" style="min-width: 350px;">
        <slot
            name="input"
            v-bind="{
              startDate,
              endDate,
              rangeText: getDisplayRangeText(startDate, endDate, rangeText),
              rawRangeText: rangeText
            }"
        >
          {{ startDate && endDate ? getDisplayRangeText(startDate, endDate, rangeText) : placeholder }}
        </slot>
      </template>
      <div slot="footer" slot-scope="{ rangeText, locale:{ cancelLabel, applyLabel, resetLabel }, clickCancel, clickApply, in_selection }" class="slot">
        <div data-v-1ebd09d2="" class="drp-buttons">
          <div class="d-flex justify-content-between">
            <div class="flex-grow-0">
              <button @click="handleReset" class="btn btn-sm btn-link m-0">{{ resetLabel }}</button>
            </div>
            <div class="flex-grow-1 d-flex justify-content-end">
              <span data-v-1ebd09d2="" class="drp-selected">{{ getDisplayRangeText(dates.startDate, dates.endDate, rangeText) }}</span>
              <button @click="clickCancel" class="cancelBtn btn btn-sm btn-secondary m-0">{{ cancelLabel }}</button>
              <button @click="clickApply" class="applyBtn btn btn-sm btn-success" :disabled="in_selection">{{ applyLabel }}</button>
            </div>
          </div>
        </div>
      </div>
    </date-range-picker>
  </b-form-group>
</template>

<script>
import DateRangePicker from 'vue2-daterange-picker'
import 'vue2-daterange-picker/dist/vue2-daterange-picker.css'

export default {
  name: 'datepicker-range',
  components: {
    DateRangePicker ,
  },
  props: {
    value: {
      type: [Array, String],
      default: () => [],
    },
    placeholder: {
      type: String,
      default: '-'
    },
    state: {type: Boolean, default: true},
    shortcuts: {
      type: [Object, Boolean],
      default() {
        let today = this.$moment()
        return {
          'Hari ini': [today.toDate(), today.toDate()],
          'Minggu ini': [today.startOf('week').toDate(), today.endOf('week').toDate()],
          'Bulan ini': [today.startOf('month').toDate(), today.endOf('month').toDate()],
          '3 bulan lalu': [this.$moment().subtract(3, 'months').add(1, 'days').toDate(), new Date()],
          '1 tahun lalu': [this.$moment().subtract(1, 'years').add(1, 'days').toDate(), new Date()],
        }
      }
    },
    localeData: {
      type: Object,
      default: function () {
        return {
          direction: 'ltr',
          format: 'dd/mm/yyyy',
          separator: ' - ',
          applyLabel: this.$t('Apply'),
          cancelLabel: this.$t('Cancel'),
          resetLabel: this.$t('Reset'),
          weekLabel: 'W',
          customRangeLabel: 'Custom Range',
          daysOfWeek: ['S', 'M', 'T', 'W', 'T', 'F', 'S'],
          monthNames: ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'],
          firstDay: 0
        }
      },
    },
    opens: {
      type: String,
      default: 'center',
    },
    appendToBody: {
      type: Boolean,
      default: true,
    }
  },
  data() {
    return {
      dates: {
        startDate: Array.isArray(this.value) ? this.value[0] ?? null : this.value.split(',') [0],
        endDate: Array.isArray(this.value) ? this.value[1] ?? null : this.value.split(',') [1],
      },
    }
  },
  watch: {
    dates(val, oldVal) {
      if (!this.lodash.isEqual(val, oldVal)) {
        let arr = []
        if (val.startDate && val.endDate) {
          arr = [this.$moment(val.startDate).format('YYYY-MM-DD'), this.$moment(val.endDate).format('YYYY-MM-DD')]
        }
        this.$emit('input', arr)
      }
    },
    value(val, oldVal){
      if (!this.lodash.isEqual(val, oldVal)) {
        if(!val?.length>0){
          this.dates.startDate = null
          this.dates.endDate = null
        }
      }
    }
  },
  methods: {
    currentLocale() {
      return this.$i18n?.locale || 'en'
    },
    normalizeDisplayFormat(format) {
      if (!format) {
        return 'DD/MM/YYYY'
      }

      return String(format)
          .replace(/yyyy/gi, 'YYYY')
          .replace(/yy/gi, 'YY')
          .replace(/mmmm/gi, 'MMMM')
          .replace(/mmm/gi, 'MMM')
          .replace(/mm/g, 'MM')
          .replace(/dd/gi, 'DD')
          .replace(/d/gi, 'D')
    },
    formatDisplayDate(date) {
      if (!date) {
        return ''
      }

      const momentDate = this.$moment(date).locale(this.currentLocale())
      if (!momentDate.isValid()) {
        return ''
      }

      return momentDate.format(this.normalizeDisplayFormat(this.localeData?.format))
    },
    getDisplayRangeText(startDate, endDate, fallbackText = '') {
      if (!startDate || !endDate) {
        return fallbackText
      }

      const startText = this.formatDisplayDate(startDate)
      const endText = this.formatDisplayDate(endDate)

      if (!startText || !endText) {
        return fallbackText
      }

      return `${startText}${this.localeData?.separator ?? ' - '}${endText}`
    },
    handleReset() {
      this.$refs['daterange-picker'].end = null;
      this.$refs['daterange-picker'].start = null;
    }
  }
}
</script>

<style lang="scss" scoped>
::v-deep{
  .vue-daterange-picker {
    .form-control {
      padding: 8px 16px !important;
      height: 36px !important;
      white-space: nowrap !important;
      text-overflow: ellipsis !important;
    }

    &.invalid {
      .reportrange-text {
        border-color: #DC1021 !important;
      }
    }

    .calendars-container {
      .calendar-table {
        th,td {
          padding: 0 8px;
          height: 32px;
          width: 36px;
        }
        td.start-date:not(.off), td.end-date:not(.off) {
          border-radius: 0;
          background-color: #0066FF;
        }

        td.in-range:not(.off, .active) {
          border-radius: 0;
          border-color: #C7E1FF;
          background-color: #C7E1FF;
        }
      }
    }

    .ranges {
      li {
        //styleName: Desktop/Body/text-lg | Regular 16|20;
        font-family: Open Sans, sans-serif;
        font-size: 16px;
        font-weight: 400;
        line-height: 20px;
        letter-spacing: 0px;
        text-align: left;


        &.active {
          color: #4899F3;
          background-color: white;
          font-weight: 700;
        }
      }
    }

    .daterangepicker {
      .drp-buttons {
        padding: 8px;

        span.drp-selected {
          display: none;
          color: #696F8C;
          //styleName: Functional/Button/B3;
          font-family: Inter;
          font-size: 14px;
          font-weight: 600;
          line-height: 24px;
          letter-spacing: -0.5px;
          text-align: left;

        }

        .btn {
          //styleName: Functional/Button/B3;
          font-family: Inter;
          font-size: 14px;
          font-weight: 600;
          line-height: 24px;
          letter-spacing: -0.5px;
          text-align: left;
          border-radius: 4px;
          padding: 4px 20px;

          &.btn.btn-secondary {
            background: #FFFFFF;
            border: 1px solid #D8DAE5 !important;
            color: #696F8C !important;
          }

          &.btn-success {
            background: #0066FF;
          }
        }
      }
    }
  }
}
</style>
